import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/cn.js'

/**
 * A word on a row. Never colour alone.
 *
 * The flow board's rule, kept: *"A status is never colour alone. Every one of these
 * is written as a word wherever it appears — on the User's booking, on the
 * provider's diary, on the Admin's table."* So a badge always has a label, and the
 * tone only reinforces it.
 *
 * The tones follow the brand's meanings rather than a palette: Menthe is *it went
 * well or it costs nothing*, Pêche is *limited, held, running out*, and the error
 * ink is a refusal. Nectarine is the action colour and is deliberately absent —
 * a badge is not something to press.
 */
/*
  `self-start w-fit`, because a badge is a pill and a pill is never the width of
  its column.

  `inline-flex` is not enough: the moment a badge is a **flex child** — which it
  is in every table cell that stacks a status over a second line — the browser
  blockifies it and it stretches to the full cross-axis. On 9.3 that made every
  status read as a 282-pixel bar across the Payments table, which is what the
  client meant by "status chips are elongated for no reason".
*/
const badge = cva(
  'inline-flex w-fit self-start items-center gap-1 rounded-full font-medium whitespace-nowrap [&_svg]:size-3',
  {
    variants: {
      tone: {
        neutral: 'bg-sunk text-ink-2',
        success: 'bg-teal-soft text-teal-text',
        attention: 'bg-amber-soft text-amber-ink',
        error: 'bg-error-soft text-error',
        info: 'bg-water-soft text-water-text',
        /** A hairline outline, for a count that is not a state. */
        outline: 'bg-transparent text-ink-2 ring-1 ring-inset ring-hairline-strong',
      },
      size: {
        sm: 'h-5 px-2 text-micro',
        md: 'h-6 px-2.5 text-caption',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'md' },
  },
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badge> {
  icon?: React.ReactNode
}

export function Badge({ className, tone, size, icon, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badge({ tone, size }), className)} {...props}>
      {icon}
      {children}
    </span>
  )
}
